import React, { useEffect, useRef, useState } from "react";
import markerIcon from "../../../assets/icon_map_locate.svg"; // 내 위치 마커 이미지 경로
import markerNbIcon from "../../../assets/icon_map_nb.svg"; // 노브랜드 마커
import markerEdIcon from "../../../assets/icon_map_ed.svg"; // 노브랜드 마커
import markerSelectedIcon from "../../../assets/icon_map_selected.svg"; // 선택된 마커 이미지 경로
import markerEDSelectedIcon from "../../../assets/icon_map_ed_selected.svg"; // 선택된 마커 이미지 경로

const { REACT_APP_NAVER_MAP_CLIENT_ID } = process.env;

const NaverMap = ({
  brandId,
  branch,
  selectBranch,
  branchList,
  setKeyword,
  location,
  setLocation,
}) => {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);
  const selectedMarkerRef = useRef(null);
  const currentLocationMarkerRef = useRef(null);

  const loadScript = (url) => {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = url;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });
  };

  const initializeMap = () => {
    if (location.lat1 !== 0 && location.lng1 !== 0) {
      if (!mapInstance.current) {
        mapInstance.current = new window.naver.maps.Map(mapRef.current, {
          center: new window.naver.maps.LatLng(location.lat1, location.lng1),
          zoom: 15,
          minZoom: 10,
          zoomControl: false,
          mapTypeControl: false,
          logoControl: false,
          mapDataControl: false,
        });
      } else {
        mapInstance.current.setCenter(
          new window.naver.maps.LatLng(location.lat1, location.lng1)
        );
      }

      // 기존 마커 제거
      markersRef.current.forEach((marker) => marker.setMap(null));
      markersRef.current = [];

      //내 위치 마커 제거
      if (currentLocationMarkerRef.current) {
        currentLocationMarkerRef.current.setMap(null);
      }
      // 현재 내 위치 마커 표시
      currentLocationMarkerRef.current = new window.naver.maps.Marker({
        position: new window.naver.maps.LatLng(location.lat1, location.lng1),
        map: mapInstance.current,
        icon: {
          url: markerIcon,
          size: new window.naver.maps.Size(37, 29),
          scaledSize: new window.naver.maps.Size(37, 29),
        },
        zIndex: 999,
      });

      // 마커 배열 표시
      markersRef.current = branchList.map((marker) => {
        const markerInstance = new window.naver.maps.Marker({
          position: new window.naver.maps.LatLng(marker.map_x, marker.map_y),
          map: mapInstance.current,
          icon: {
            url: brandId === "NB" ? markerNbIcon : markerEdIcon,
            size: new window.naver.maps.Size(37, 29),
            scaledSize: new window.naver.maps.Size(37, 29),
          },
        });

        window.naver.maps.Event.addListener(markerInstance, "click", () => {
          selectBranch(marker.id); // 브랜치 선택
        });

        return markerInstance;
      });
    }
  };

  useEffect(() => {
    loadScript(
      `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${REACT_APP_NAVER_MAP_CLIENT_ID}&submodules=geocoder`
    )
      .then(() => {
        const checkNaverMapsLoaded = () => {
          if (
            window.naver &&
            window.naver.maps &&
            window.naver.maps.Service?.reverseGeocode
          ) {
            getCurrentLocation();
          } else {
            setTimeout(checkNaverMapsLoaded, 100);
          }
        };

        checkNaverMapsLoaded();
      })
      .catch((error) => {
        console.error("Failed to load Naver Map script:", error);
      });
  }, []);

  useEffect(() => {
    if (location.lat1 !== 0 && location.lng1 !== 0 && window.naver?.maps) {
      initializeMap();
    }
  }, [location]);

  useEffect(() => {
    if (branch?.id && markersRef.current.length > 0) {
      const selectedBranch = branchList.find((b) => b.id === branch.id);
      if (selectedBranch) {
        const newCenter = new window.naver.maps.LatLng(
          selectedBranch.map_x,
          selectedBranch.map_y
        );
        mapInstance.current.panTo(newCenter);

        // 마커 아이콘 변경
        markersRef.current.forEach((marker) => {
          if (marker.getPosition().equals(newCenter)) {
            marker.setIcon({
              url: brandId === "NB" ? markerSelectedIcon : markerEDSelectedIcon,
              size: new window.naver.maps.Size(37, 29),
              scaledSize: new window.naver.maps.Size(37, 29),
            });
            selectedMarkerRef.current = marker;
          } else {
            marker.setIcon({
              url: brandId === "NB" ? markerNbIcon : markerEdIcon,
              size: new window.naver.maps.Size(37, 29),
              scaledSize: new window.naver.maps.Size(37, 29),
            });
          }
        });
      }
    }
  }, [branch, branchList]);

  useEffect(() => {
    if (location.lat1 !== 0 && location.lng1 !== 0 && window.naver?.maps) {
      initializeMap();
    }
  }, [branchList]);

  const getCityNameFromCoordinates = ({ lat1, lng1 }) => {
    const latlng = new window.naver.maps.LatLng(lat1, lng1);
    window.naver.maps.Service.reverseGeocode(
      {
        coords: latlng,
        orders: [
          window.naver.maps.Service.OrderType.ADDR,
          window.naver.maps.Service.OrderType.ROAD_ADDR,
        ].join(","),
      },
      function (status, response) {
        if (status === window.naver.maps.Service.Status.ERROR) {
          return console.error("Geocoding failed: " + status);
        }

        const items = response.v2.results;
        if (items.length > 0) {
          const address1 = items[0].region.area1.name;
          const address2 = items[0].region.area2.name;
          setKeyword(`${address1} ${address2}`);
        }
      }
    );
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat1: position.coords.latitude,
            lng1: position.coords.longitude,
          });
          getCityNameFromCoordinates({
            lat1: position.coords.latitude,
            lng1: position.coords.longitude,
          });
        },
        () => {
          setLocation({ lat1: 37.5666103, lng1: 126.9783882 });
          getCityNameFromCoordinates({ lat1: 37.5666103, lng1: 126.9783882 });
        }
      );
    } else {
      console.error("Geolocation is not supported.");
    }
  };

  return (
    <div className="relative flex-1 w-full bg-slate-400">
      <div id="map" ref={mapRef} className="w-full h-full"></div>
      <button
        onClick={getCurrentLocation}
        className="absolute p-2 text-sm text-white bg-blue-500 rounded top-2 right-2"
      >
        현재 위치 찾기
      </button>
    </div>
  );
};

export default NaverMap;
